import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { Plus, Shield } from "lucide-react";

interface Permiso {
  id: string;
  nombre: string;
  descripcion: string;
  categoria: string;
}

interface RolConfig {
  nombre: string;
  permisos: string[];
  descripcion: string;
}

const PERMISOS_DISPONIBLES: Permiso[] = [
  // Gestión de Usuarios
  { id: "ver_usuarios", nombre: "Ver usuarios", descripcion: "Ver lista de usuarios del sistema", categoria: "Usuarios" },
  { id: "crear_usuarios", nombre: "Crear usuarios", descripcion: "Registrar nuevos usuarios", categoria: "Usuarios" },
  { id: "editar_usuarios", nombre: "Editar usuarios", descripcion: "Modificar información de usuarios", categoria: "Usuarios" },
  { id: "eliminar_usuarios", nombre: "Eliminar usuarios", descripcion: "Eliminar usuarios del sistema", categoria: "Usuarios" },
  { id: "asignar_roles", nombre: "Asignar roles", descripcion: "Asignar y modificar roles de usuarios", categoria: "Usuarios" },
  
  // Obligaciones Ambientales
  { id: "ver_obligaciones", nombre: "Ver obligaciones", descripcion: "Consultar obligaciones ambientales", categoria: "Ambiental" },
  { id: "crear_obligaciones", nombre: "Crear obligaciones", descripcion: "Registrar nuevas obligaciones", categoria: "Ambiental" },
  { id: "editar_obligaciones", nombre: "Editar obligaciones", descripcion: "Modificar obligaciones existentes", categoria: "Ambiental" },
  { id: "eliminar_obligaciones", nombre: "Eliminar obligaciones", descripcion: "Eliminar obligaciones", categoria: "Ambiental" },
  
  // Evidencias
  { id: "ver_evidencias", nombre: "Ver evidencias", descripcion: "Consultar evidencias de cumplimiento", categoria: "Evidencias" },
  { id: "subir_evidencias", nombre: "Subir evidencias", descripcion: "Cargar nuevas evidencias", categoria: "Evidencias" },
  { id: "eliminar_evidencias", nombre: "Eliminar evidencias", descripcion: "Eliminar evidencias", categoria: "Evidencias" },
  
  // Documentos Legales
  { id: "ver_documentos_legales", nombre: "Ver documentos legales", descripcion: "Consultar documentos legales", categoria: "Legal" },
  { id: "subir_documentos_legales", nombre: "Subir documentos legales", descripcion: "Cargar nuevos documentos", categoria: "Legal" },
  { id: "eliminar_documentos_legales", nombre: "Eliminar documentos legales", descripcion: "Eliminar documentos", categoria: "Legal" },
  
  // Reportes
  { id: "generar_reportes", nombre: "Generar reportes", descripcion: "Crear reportes del sistema", categoria: "Reportes" },
  { id: "exportar_reportes", nombre: "Exportar reportes", descripcion: "Exportar reportes a PDF/Excel", categoria: "Reportes" },
  
  // Configuración
  { id: "acceso_configuracion", nombre: "Acceso a configuración", descripcion: "Acceder al módulo de configuración", categoria: "Sistema" },
  { id: "gestionar_roles", nombre: "Gestionar roles", descripcion: "Crear y modificar roles del sistema", categoria: "Sistema" },
];

const ROLES_PREDEFINIDOS: Record<string, RolConfig> = {
  admin: {
    nombre: "Administrador",
    descripcion: "Acceso completo al sistema",
    permisos: PERMISOS_DISPONIBLES.map(p => p.id),
  },
  moderator: {
    nombre: "Moderador",
    descripcion: "Permisos de gestión limitados",
    permisos: [
      "ver_usuarios",
      "ver_obligaciones",
      "crear_obligaciones",
      "editar_obligaciones",
      "ver_evidencias",
      "subir_evidencias",
      "ver_documentos_legales",
      "subir_documentos_legales",
      "generar_reportes",
      "exportar_reportes",
    ],
  },
  user: {
    nombre: "Usuario",
    descripcion: "Permisos básicos de consulta",
    permisos: [
      "ver_obligaciones",
      "ver_evidencias",
      "subir_evidencias",
      "ver_documentos_legales",
      "generar_reportes",
    ],
  },
};

export const ConfiguracionRoles = () => {
  const { toast } = useToast();
  const [rolSeleccionado, setRolSeleccionado] = useState<string>("admin");
  const [permisosActivos, setPermisosActivos] = useState<string[]>([]);

  useEffect(() => {
    // Cargar permisos del rol seleccionado
    const config = ROLES_PREDEFINIDOS[rolSeleccionado];
    if (config) {
      setPermisosActivos(config.permisos);
    }
  }, [rolSeleccionado]);

  const togglePermiso = (permisoId: string) => {
    setPermisosActivos(prev =>
      prev.includes(permisoId)
        ? prev.filter(p => p !== permisoId)
        : [...prev, permisoId]
    );
  };

  const guardarConfiguracion = () => {
    // Guardar en localStorage (en producción, esto iría a la base de datos)
    const config = {
      ...ROLES_PREDEFINIDOS,
      [rolSeleccionado]: {
        ...ROLES_PREDEFINIDOS[rolSeleccionado],
        permisos: permisosActivos,
      },
    };
    
    localStorage.setItem('roles_config', JSON.stringify(config));
    
    toast({
      title: "Configuración guardada",
      description: `Los permisos del rol ${ROLES_PREDEFINIDOS[rolSeleccionado].nombre} han sido actualizados`,
    });
  };

  const categorias = [...new Set(PERMISOS_DISPONIBLES.map(p => p.categoria))];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Configuración de Roles y Permisos
          </CardTitle>
          <CardDescription>
            Define qué puede hacer cada rol en el sistema
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Selector de Rol */}
          <div className="space-y-3">
            <Label>Seleccionar Rol</Label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(ROLES_PREDEFINIDOS).map(([key, config]) => (
                <Button
                  key={key}
                  variant={rolSeleccionado === key ? "default" : "outline"}
                  onClick={() => setRolSeleccionado(key)}
                  className="h-auto flex-col items-start p-4"
                >
                  <span className="font-semibold">{config.nombre}</span>
                  <span className="text-xs text-muted-foreground">
                    {config.descripcion}
                  </span>
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          {/* Permisos por Categoría */}
          <div className="space-y-6">
            {categorias.map(categoria => (
              <div key={categoria} className="space-y-3">
                <h3 className="font-semibold text-lg">{categoria}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {PERMISOS_DISPONIBLES
                    .filter(p => p.categoria === categoria)
                    .map(permiso => (
                      <div
                        key={permiso.id}
                        className="flex items-start space-x-3 p-3 rounded-lg border bg-card"
                      >
                        <Checkbox
                          id={permiso.id}
                          checked={permisosActivos.includes(permiso.id)}
                          onCheckedChange={() => togglePermiso(permiso.id)}
                          disabled={rolSeleccionado === "admin"} // Admin siempre tiene todos los permisos
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor={permiso.id}
                            className="text-sm font-medium cursor-pointer"
                          >
                            {permiso.nombre}
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            {permiso.descripcion}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>

          <Separator />

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setPermisosActivos(ROLES_PREDEFINIDOS[rolSeleccionado].permisos)}
            >
              Restaurar por defecto
            </Button>
            <Button onClick={guardarConfiguracion}>
              Guardar configuración
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Instrucciones para crear nuevos roles */}
      <Card>
        <CardHeader>
          <CardTitle>Crear Nuevos Roles</CardTitle>
          <CardDescription>
            Para crear un nuevo rol en el sistema, necesitas ejecutar una migración SQL
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted p-4 rounded-lg">
            <p className="text-sm mb-2">Ejemplo de SQL para agregar un nuevo rol:</p>
            <pre className="text-xs bg-background p-3 rounded overflow-x-auto">
{`-- Agregar nuevo valor al enum
ALTER TYPE public.app_role ADD VALUE 'supervisor';

-- El nuevo rol estará disponible inmediatamente
-- Luego puedes asignarlo a usuarios desde Gestión de Usuarios`}
            </pre>
          </div>
          
          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              <strong>Nota:</strong> Los valores del enum no se pueden eliminar una vez agregados.
              Solo puedes agregar nuevos roles.
            </p>
            <p>
              Roles actuales disponibles: <code className="bg-muted px-2 py-1 rounded">admin</code>,{" "}
              <code className="bg-muted px-2 py-1 rounded">moderator</code>,{" "}
              <code className="bg-muted px-2 py-1 rounded">user</code>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
